import React from "react";

export type PatientJourneyKind =
  | "order"
  | "acquisition"
  | "event"
  | "report"
  | "amendment"
  | "communication"
  | "billing";

export type PatientJourneyMilestone = {
  id: string;
  kind: PatientJourneyKind;
  occurredAt: string;
  label: string;
};

export function buildStudyJourneyMilestones(study: {
  id: string;
  studyDate: string;
  status: string;
  indication?: string;
  findings?: string;
  technician?: string;
}): PatientJourneyMilestone[] {
  const start = study.studyDate || new Date(0).toISOString();
  const plusHours = (hours: number) => {
    const millis = Date.parse(start);
    if (!Number.isFinite(millis)) return start;
    return new Date(millis + hours * 3600_000).toISOString();
  };
  return [
    { id: `${study.id}-order`, kind: "order", occurredAt: start, label: "Order received" },
    { id: `${study.id}-acq`, kind: "acquisition", occurredAt: plusHours(1), label: study.technician ? `Acquired by ${study.technician}` : "Acquisition complete" },
    { id: `${study.id}-event`, kind: "event", occurredAt: plusHours(2), label: study.indication || "Clinical events reviewed" },
    { id: `${study.id}-report`, kind: "report", occurredAt: plusHours(3), label: `Report ${study.status}` },
    { id: `${study.id}-amend`, kind: "amendment", occurredAt: plusHours(4), label: study.findings ? "Findings updated" : "No amendment" },
    { id: `${study.id}-comm`, kind: "communication", occurredAt: plusHours(5), label: "Results communication" },
    { id: `${study.id}-bill`, kind: "billing", occurredAt: plusHours(6), label: "Billing milestone" },
  ];
}

export type PatientJourneyTimelinePanelProps = {
  patientLabel: string;
  studyId: string;
  milestones: PatientJourneyMilestone[];
};

export default function PatientJourneyTimelinePanel({
  patientLabel,
  studyId,
  milestones,
}: PatientJourneyTimelinePanelProps) {
  return (
    <section data-testid="patient-journey-timeline" aria-label="Patient journey timeline">
      <h3 className="h6">Patient journey</h3>
      <p className="text-muted small mb-2">
        {patientLabel} - study {studyId}
      </p>
      <ol className="list-group list-group-numbered">
        {milestones.map((milestone) => (
          <li key={milestone.id} className="list-group-item d-flex justify-content-between">
            <span>
              <strong className="text-uppercase me-2">{milestone.kind}</strong>
              {milestone.label}
            </span>
            <time dateTime={milestone.occurredAt}>{milestone.occurredAt.replace("T", " ").replace(".000Z", "Z")}</time>
          </li>
        ))}
      </ol>
    </section>
  );
}
